import React, { useMemo, useState } from 'react';
import {
  Users,
  Calendar,
  Activity,
  Percent,
  TrendingUp,
  BarChart3,
  Table as TableIcon,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Download,
  Flame,
} from 'lucide-react';
import {
  Area,
  AreaChart,
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { chartAxisTick, chartTooltipStyle, formatNumber, formatPct } from './biTheme';

const BLUE = '#3b82f6';
const PURPLE = '#8b5cf6';
const EMERALD = '#10b981';
const AMBER = '#f59e0b';

const ActiveUsersStickinessPanel = ({ activeUserMetrics = null, isRefreshing = false }) => {
  const [viewMode, setViewMode] = useState('chart'); // 'chart' | 'table'
  const [chartType, setChartType] = useState('stickiness'); // 'stickiness' | 'volume'
  const [sortKey, setSortKey] = useState('date');
  const [sortDir, setSortDir] = useState('desc');

  const summary = activeUserMetrics?.summary || {};
  const daywise = useMemo(() => activeUserMetrics?.daywise || [], [activeUserMetrics]);

  const sortedRows = useMemo(() => {
    const list = [...daywise];
    list.sort((a, b) => {
      let av = a[sortKey];
      let bv = b[sortKey];
      if (typeof av === 'string' || typeof bv === 'string') {
        const cmp = String(av || '').localeCompare(String(bv || ''));
        return sortDir === 'asc' ? cmp : -cmp;
      }
      av = Number(av || 0);
      bv = Number(bv || 0);
      return sortDir === 'asc' ? av - bv : bv - av;
    });
    return list;
  }, [daywise, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(key);
    setSortDir('desc');
  };

  const SortIcon = ({ columnKey }) => {
    if (sortKey !== columnKey) return <ArrowUpDown className="h-3 w-3 opacity-30" />;
    return sortDir === 'asc' ? <ArrowUp className="h-3 w-3 text-purple-600" /> : <ArrowDown className="h-3 w-3 text-purple-600" />;
  };

  const handleExportCsv = () => {
    if (!daywise.length) return;
    const headers = ['Date', 'DAU', 'WAU (7 Days)', 'MAU (30 Days)', 'DAU/WAU Ratio (%)', 'DAU/MAU Ratio (%)'];
    const rows = sortedRows.map((r) => [
      r.date,
      r.dau,
      r.wau,
      r.mau,
      r.dauWauRatio,
      r.dauMauRatio,
    ]);
    const footer = [
      'Average',
      summary.avgDau ?? 0,
      summary.avgWau ?? 0,
      summary.avgMau ?? 0,
      summary.avgDauWauRatio ?? 0,
      summary.avgDauMauRatio ?? 0,
    ];
    const csvContent = [headers.join(','), ...rows.map((row) => row.join(',')), footer.join(',')].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `onemart_user_stickiness_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (!daywise.length && !summary.latestWau) {
    return null;
  }

  return (
    <div className="space-y-3 rounded-2xl border border-slate-100 bg-white p-4 shadow-[0_6px_20px_rgba(15,23,42,0.04)]">
      {/* Section Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 pb-3">
        <div>
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-purple-50 text-purple-600">
              <Flame className="h-4 w-4" />
            </div>
            <div>
              <h3 className="text-base font-bold text-slate-900">Active Users & Stickiness (DAU, WAU, MAU)</h3>
              <p className="text-xs text-slate-400">
                Weekly Active Users (last 7 days), Monthly Active Users (last 30 days) and Daywise stickiness
              </p>
            </div>
          </div>
        </div>

        {/* View Switchers */}
        <div className="flex items-center gap-2">
          {viewMode === 'chart' ? (
            <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5 text-xs font-semibold">
              <button
                type="button"
                onClick={() => setChartType('stickiness')}
                className={`rounded-md px-2.5 py-1 ${
                  chartType === 'stickiness' ? 'bg-white text-purple-700 shadow-sm' : 'text-slate-500'
                }`}
              >
                Stickiness Ratios (%)
              </button>
              <button
                type="button"
                onClick={() => setChartType('volume')}
                className={`rounded-md px-2.5 py-1 ${
                  chartType === 'volume' ? 'bg-white text-purple-700 shadow-sm' : 'text-slate-500'
                }`}
              >
                User Volumes (Count)
              </button>
            </div>
          ) : null}

          <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5 text-xs font-semibold">
            <button
              type="button"
              onClick={() => setViewMode('chart')}
              className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1 ${
                viewMode === 'chart' ? 'bg-white text-purple-700 shadow-sm' : 'text-slate-500'
              }`}
            >
              <BarChart3 className="h-3.5 w-3.5" /> Chart
            </button>
            <button
              type="button"
              onClick={() => setViewMode('table')}
              className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1 ${
                viewMode === 'table' ? 'bg-white text-purple-700 shadow-sm' : 'text-slate-500'
              }`}
            >
              <TableIcon className="h-3.5 w-3.5" /> Table Form
            </button>
          </div>

          <button
            type="button"
            onClick={handleExportCsv}
            title="Export Stickiness Data to CSV"
            className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-600 shadow-sm hover:bg-slate-50"
          >
            <Download className="h-3.5 w-3.5" /> Export
          </button>
        </div>
      </div>

      {/* KPI Cards Row */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-5">
        {/* WAU Last 7 Days */}
        <div className="relative overflow-hidden rounded-xl border border-slate-100 bg-gradient-to-br from-white to-slate-50 p-3 shadow-sm">
          <span className="absolute inset-y-0 left-0 w-1 bg-gradient-to-b from-blue-500 to-indigo-600" aria-hidden />
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span className="font-semibold uppercase tracking-wider">WAU (Last 7 Days)</span>
            <Users className="h-4 w-4 text-blue-500" />
          </div>
          <p className="mt-1 text-2xl font-black text-slate-900">
            {formatNumber(summary.latestWau || 0)}
          </p>
          <p className="mt-0.5 text-xs text-slate-400">
            Range Avg: <strong className="text-slate-600">{formatNumber(summary.avgWau || 0)}</strong>
          </p>
        </div>

        {/* MAU Last 30 Days */}
        <div className="relative overflow-hidden rounded-xl border border-slate-100 bg-gradient-to-br from-white to-slate-50 p-3 shadow-sm">
          <span className="absolute inset-y-0 left-0 w-1 bg-gradient-to-b from-purple-500 to-violet-600" aria-hidden />
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span className="font-semibold uppercase tracking-wider">MAU (Last 30 Days)</span>
            <Calendar className="h-4 w-4 text-purple-500" />
          </div>
          <p className="mt-1 text-2xl font-black text-slate-900">
            {formatNumber(summary.latestMau || 0)}
          </p>
          <p className="mt-0.5 text-xs text-slate-400">
            Range Avg: <strong className="text-slate-600">{formatNumber(summary.avgMau || 0)}</strong>
          </p>
        </div>

        {/* DAU / WAU Ratio */}
        <div className="relative overflow-hidden rounded-xl border border-slate-100 bg-gradient-to-br from-white to-slate-50 p-3 shadow-sm">
          <span className="absolute inset-y-0 left-0 w-1 bg-gradient-to-b from-emerald-500 to-teal-600" aria-hidden />
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span className="font-semibold uppercase tracking-wider">DAU / WAU Ratio</span>
            <Percent className="h-4 w-4 text-emerald-500" />
          </div>
          <p className="mt-1 text-2xl font-black text-slate-900">
            {formatPct(summary.latestDauWauRatio || 0, 1)}
          </p>
          <p className="mt-0.5 text-xs text-slate-400">
            Range Avg: <strong className="text-emerald-700 font-semibold">{formatPct(summary.avgDauWauRatio || 0, 1)}</strong>
          </p>
        </div>

        {/* DAU / MAU Ratio */}
        <div className="relative overflow-hidden rounded-xl border border-slate-100 bg-gradient-to-br from-white to-slate-50 p-3 shadow-sm">
          <span className="absolute inset-y-0 left-0 w-1 bg-gradient-to-b from-amber-500 to-orange-600" aria-hidden />
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span className="font-semibold uppercase tracking-wider">DAU / MAU Stickiness</span>
            <Activity className="h-4 w-4 text-amber-500" />
          </div>
          <p className="mt-1 text-2xl font-black text-slate-900">
            {formatPct(summary.latestDauMauRatio || 0, 1)}
          </p>
          <p className="mt-0.5 text-xs text-slate-400">
            Range Avg: <strong className="text-amber-700 font-semibold">{formatPct(summary.avgDauMauRatio || 0, 1)}</strong>
          </p>
        </div>

        {/* Average DAU */}
        <div className="relative overflow-hidden rounded-xl border border-slate-100 bg-gradient-to-br from-white to-slate-50 p-3 shadow-sm col-span-2 sm:col-span-1">
          <span className="absolute inset-y-0 left-0 w-1 bg-gradient-to-b from-slate-400 to-slate-600" aria-hidden />
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span className="font-semibold uppercase tracking-wider">Average DAU</span>
            <TrendingUp className="h-4 w-4 text-slate-500" />
          </div>
          <p className="mt-1 text-2xl font-black text-slate-900">
            {formatNumber(summary.avgDau || 0)}
          </p>
          <p className="mt-0.5 text-xs text-slate-400">
            Latest DAU: <strong className="text-slate-600">{formatNumber(summary.latestDau || 0)}</strong>
          </p>
        </div>
      </div>

      {/* Main Content Area: Chart or Table */}
      {viewMode === 'chart' ? (
        <div className="h-72 w-full pt-2">
          {chartType === 'stickiness' ? (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={daywise} margin={{ top: 10, right: 12, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="dauWauGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={EMERALD} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={EMERALD} stopOpacity={0.02} />
                  </linearGradient>
                  <linearGradient id="dauMauGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={PURPLE} stopOpacity={0.3} />
                    <stop offset="100%" stopColor={PURPLE} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="date" stroke="#94a3b8" tick={chartAxisTick} />
                <YAxis stroke="#94a3b8" tick={chartAxisTick} unit="%" />
                <Tooltip
                  contentStyle={chartTooltipStyle}
                  formatter={(val, name) => [
                    formatPct(val, 2),
                    name === 'dauWauRatio' ? 'DAU / WAU Ratio' : 'DAU / MAU Stickiness',
                  ]}
                />
                <Legend wrapperStyle={{ fontSize: 13 }} />
                <Area
                  type="monotone"
                  dataKey="dauWauRatio"
                  name="DAU / WAU (%)"
                  stroke={EMERALD}
                  fill="url(#dauWauGrad)"
                  strokeWidth={2.5}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                />
                <Area
                  type="monotone"
                  dataKey="dauMauRatio"
                  name="DAU / MAU (%)"
                  stroke={PURPLE}
                  fill="url(#dauMauGrad)"
                  strokeWidth={2.5}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={daywise} margin={{ top: 10, right: 12, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="date" stroke="#94a3b8" tick={chartAxisTick} />
                <YAxis stroke="#94a3b8" tick={chartAxisTick} />
                <Tooltip
                  contentStyle={chartTooltipStyle}
                  formatter={(val, name) => [formatNumber(val), name]}
                />
                <Legend wrapperStyle={{ fontSize: 13 }} />
                <Bar dataKey="dau" name="DAU" fill="#93c5fd" radius={[4, 4, 0, 0]} barSize={20} />
                <Line type="monotone" dataKey="wau" name="WAU (7d)" stroke={BLUE} strokeWidth={2.5} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="mau" name="MAU (30d)" stroke={PURPLE} strokeWidth={2.5} dot={{ r: 3 }} />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </div>
      ) : (
        /* Table Form with Summary Footer */
        <div className="max-h-80 overflow-auto rounded-xl border border-slate-200">
          <table className="min-w-full divide-y divide-slate-200 text-left text-xs">
            <thead className="sticky top-0 bg-slate-50 font-bold uppercase tracking-wider text-slate-600 shadow-sm">
              <tr>
                <th className="px-3 py-2.5">
                  <button type="button" onClick={() => toggleSort('date')} className="inline-flex items-center gap-1">
                    Date <SortIcon columnKey="date" />
                  </button>
                </th>
                <th className="px-3 py-2.5 text-right">
                  <button type="button" onClick={() => toggleSort('dau')} className="inline-flex items-center gap-1">
                    DAU <SortIcon columnKey="dau" />
                  </button>
                </th>
                <th className="px-3 py-2.5 text-right">
                  <button type="button" onClick={() => toggleSort('wau')} className="inline-flex items-center gap-1">
                    WAU (7 Days) <SortIcon columnKey="wau" />
                  </button>
                </th>
                <th className="px-3 py-2.5 text-right">
                  <button type="button" onClick={() => toggleSort('mau')} className="inline-flex items-center gap-1">
                    MAU (30 Days) <SortIcon columnKey="mau" />
                  </button>
                </th>
                <th className="px-3 py-2.5 text-right">
                  <button type="button" onClick={() => toggleSort('dauWauRatio')} className="inline-flex items-center gap-1">
                    DAU / WAU (%) <SortIcon columnKey="dauWauRatio" />
                  </button>
                </th>
                <th className="px-3 py-2.5 text-right">
                  <button type="button" onClick={() => toggleSort('dauMauRatio')} className="inline-flex items-center gap-1">
                    DAU / MAU (%) <SortIcon columnKey="dauMauRatio" />
                  </button>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {sortedRows.map((r) => (
                <tr key={r.date} className="hover:bg-slate-50/80 transition-colors">
                  <td className="px-3 py-2 font-medium text-slate-800 whitespace-nowrap">{r.date}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-700 font-semibold">{formatNumber(r.dau)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-700">{formatNumber(r.wau)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-700">{formatNumber(r.mau)}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold text-emerald-600">
                    {formatPct(r.dauWauRatio, 2)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold text-purple-600">
                    {formatPct(r.dauMauRatio, 2)}
                  </td>
                </tr>
              ))}
            </tbody>
            {/* Aggregate Summary Footer */}
            <tfoot className="sticky bottom-0 border-t-2 border-slate-300 bg-slate-100 font-bold text-slate-900 shadow-sm">
              <tr>
                <td className="px-3 py-2.5 uppercase tracking-wider text-slate-800">
                  Average (Aggregate)
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums text-slate-900">
                  {formatNumber(summary.avgDau || 0)}
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums text-slate-900">
                  {formatNumber(summary.avgWau || 0)}
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums text-slate-900">
                  {formatNumber(summary.avgMau || 0)}
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums text-emerald-700 font-extrabold">
                  {formatPct(summary.avgDauWauRatio || 0, 2)}
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums text-purple-700 font-extrabold">
                  {formatPct(summary.avgDauMauRatio || 0, 2)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
};

export default ActiveUsersStickinessPanel;

